import type React from 'react';

import { HeadsetMicRounded } from '@mui/icons-material';
import { JsonSchemaInput } from '@ragu-components/json-schema-input';
import {
  OrdCreateInDialogButton,
  OrdFormList,
  OrdIconStandard,
  OrdReferenceInput,
  OrdTabbedForm,
  useFormType,
} from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { CallRecordingConfCreateFields, dataSchema } from '@ragu-components/ord-types';
import {
  SelectInput,
  TabbedForm,
  TextInput,
  useCanAccess,
  useRecordContext,
  useRefresh,
  useTranslate,
} from 'react-admin';

import { OrdDescriptionInput } from 'src/components/formInputs';

import { AssignUserRecordingConfsForm } from './AssignUserRecordingConfsForm';
import { UserCallRecordingConfsEmbeddedList } from './UserCallRecordingConfsEmbeddedList';

import type { OrdTabbedFormProps } from '@ragu-components/ord-components';
import type { CallRecordingConf } from '@ragu-components/ord-types';

export const CallRecordingConfsForm = (props: OrdTabbedFormProps): React.ReactElement => {
  const { create } = useFormType();
  const record = useRecordContext<CallRecordingConf>();
  const translate = useTranslate();
  const refresh = useRefresh();
  const { canAccess: canManageUsers, isPending: isPermissionPending } = useCanAccess({
    resource: Resources.call_recording_confs,
    action: 'create',
  });

  return (
    <OrdTabbedForm
      sanitizeFields
      replaceRelationships
      createJsonSchema={dataSchema.CallRecordingConfCreate}
      editJsonSchema={dataSchema.CallRecordingConfUpdate}
      {...props}
    >
      <TabbedForm.Tab label='resources.call_recording_confs.operations.sections.details'>
        <OrdFormList icon={<OrdIconStandard name='edit' />}>
          <TextInput source={CallRecordingConfCreateFields.name} isRequired resettable={create} autoComplete='off' />
          <OrdDescriptionInput source={CallRecordingConfCreateFields.description} autoComplete='off' />
        </OrdFormList>
        <OrdFormList icon={<OrdIconStandard name='volume' />}>
          <OrdReferenceInput resource={Resources.soundfiles} source={CallRecordingConfCreateFields.soundfile_id} />
        </OrdFormList>
      </TabbedForm.Tab>

      <TabbedForm.Tab label='resources.call_recording_confs.operations.sections.settings'>
        <OrdFormList icon={<OrdIconStandard name='settings' />}>
          <JsonSchemaInput source={CallRecordingConfCreateFields['settings.announce']} />
          <JsonSchemaInput source={CallRecordingConfCreateFields['settings.bip']} />
          {/* <JsonSchemaInput source={CallRecordingConfCreateFields['settings.asr']} /> */}
          {/* format values (wav/mp3) are technical — displayed as-is, not translated */}
          <SelectInput
            source={CallRecordingConfCreateFields['settings.format_file']}
            choices={dataSchema.CallRecordingConfSettings.properties.format_file.enum.map((f) => ({ id: f, name: f }))}
            defaultValue={dataSchema.CallRecordingConfSettings.properties.format_file.default}
            variant='outlined'
          />
          <JsonSchemaInput source={CallRecordingConfCreateFields['settings.name_file']} />
          {/* <JsonSchemaInput source={CallRecordingConfCreateFields['settings.hash_file']} />
          <JsonSchemaInput source={CallRecordingConfCreateFields['settings.crypto']} />
          <JsonSchemaInput source={CallRecordingConfCreateFields['settings.crypto_password']} />
          <JsonSchemaInput source={CallRecordingConfCreateFields['settings.crypto_password_confirmation']} />
          <JsonSchemaInput source={CallRecordingConfCreateFields['settings.ek']} />
          <JsonSchemaInput source={CallRecordingConfCreateFields['settings.ek_recovery']} /> */}
        </OrdFormList>
      </TabbedForm.Tab>

      {record !== null && record !== undefined && (
        <TabbedForm.Tab
          label='resources.call_recording_confs.operations.sections.users'
          sx={{ '& .list-page': { width: '100%' }, '& .RaDatagrid-tableWrapper': { height: 'auto' } }}
        >
          {!isPermissionPending && canManageUsers === true && (
            <OrdCreateInDialogButton
              resource={Resources.call_recording_confs}
              fullWidth
              label='resources.call_recording_confs.operations.manage_users'
              icon={<HeadsetMicRounded />}
              title={translate('resources.call_recording_confs.operations.manage_users', {
                _: 'Manage user assignments',
              })}
              ButtonProps={{ variant: 'contained' }}
            >
              <AssignUserRecordingConfsForm
                confId={String(record.id)}
                onUpdate={() => {
                  refresh();
                }}
              />
            </OrdCreateInDialogButton>
          )}
          <UserCallRecordingConfsEmbeddedList confId={record.id} />
        </TabbedForm.Tab>
      )}
    </OrdTabbedForm>
  );
};
